/**
 * Design System
 *
 * The kitchen sink of all design components. Essentially, we build up a
 * `components` object that is exported that contains every component in our
 * design system that should be available.
 *
 * There is a little bit of "magic" below, so be sure to read each section.
 *
 * If you prefer NO MAGIC, then simply modify this file so that you import each
 * component manually, and provide keys to the `components` module, ie.
 *
 *   import * as vueWidget from '@molecules/vue-widget';
 *   import * as card from '@molecules/card';
 *   // ...
 *   export const components = {
 *     vueWidget,
 *     card,
 *     // ...
 *   };
 */

/* Atoms */
import * as alert from './_patterns/01-atoms/alert';
import * as badge from './_patterns/01-atoms/badge';
import * as branding from './_patterns/01-atoms/branding';
import * as breadcrumb from './_patterns/01-atoms/breadcrumb';
import * as button from './_patterns/01-atoms/button';
import * as dropdown from './_patterns/01-atoms/dropdown';
import * as forms from './_patterns/01-atoms/forms';
import * as grid from './_patterns/01-atoms/grid';
import * as image from './_patterns/01-atoms/image';
import * as listGroup from './_patterns/01-atoms/list-group';
import * as svg from './_patterns/01-atoms/svg';
import * as title from './_patterns/01-atoms/title';

/* Molecules */
import * as card from './_patterns/02-molecules/card';
import * as carousel from './_patterns/02-molecules/carousel';
import * as ctacallout from './_patterns/02-molecules/leadership-card';
import * as communityNewsroom from './_patterns/02-molecules/community-newsroom';
import * as jumbotron from './_patterns/02-molecules/jumbotron';
import * as nav from './_patterns/02-molecules/nav';
import * as pagination from './_patterns/02-molecules/pagination';
import * as pageTitle from './_patterns/02-molecules/page-title';
import * as promoCallout from './_patterns/02-molecules/promo-callout';
import * as newsroomTeaser from './_patterns/02-molecules/newsroom-teaser';
import * as pressReleaseTeaser from './_patterns/02-molecules/press-release-teaser';
import * as apartmentTeaser from './_patterns/02-molecules/apartment-teaser';
import * as commSearchTeaser from './_patterns/02-molecules/community-search-teaser';
import * as youtubeModal from './_patterns/02-molecules/youtube-modal';
// import * as tabs from './_patterns/02-molecules/tabs';

/* Organisms */
import * as accordion from './_patterns/03-organisms/accordion';
import * as article from './_patterns/03-organisms/article';
import * as cardGrid from './_patterns/03-organisms/card-grid';
import * as footer from './_patterns/03-organisms/footer';
import * as navBar from './_patterns/03-organisms/navbar';
import * as testimonials from './_patterns/03-organisms/testimonial-carousel';
import * as slideshow from './_patterns/03-organisms/video-slideshow';

export const components = {
  alert,
  badge,
  branding,
  breadcrumb,
  button,
  dropdown,
  forms,
  grid,
  image,
  listGroup,
  svg,
  card,
  ctacallout,
  carousel,
  communityNewsroom,
  jumbotron,
  nav,
  pagination,
  accordion,
  article,
  cardGrid,
  title,
  pageTitle,
  promoCallout,
  newsroomTeaser,
  pressReleaseTeaser,
  apartmentTeaser,
  commSearchTeaser,
  youtubeModal,
  // tabs,
  testimonials,
  slideshow,
  footer,
  navBar,
};

/**
 * All component names as an array
 * @returns {Array} List of components name strings
 */
export const componentNames = () =>
  Object.values(components).map(({ name }) => name);

/**
 * Enable all components against a piece of DOM with some settings
 */
export const enableAllComponents = ($dom, settings) =>
  Object.values(components).forEach(({ enable }) => enable($dom, settings));

export default components;
